import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Badge,
  Button,
  Label,
  RichInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@izicvs/ui";
import { cn } from "@izicvs/utils";
import { t } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import {
  AddressBook,
  Briefcase,
  ChatCircleText,
  CheckCircle,
  CircleNotch,
  ClockCounterClockwise,
  Copy,
  Crown,
  Envelope,
  FileMagnifyingGlass,
  Kanban,
  LinkedinLogo,
  ListChecks,
  MagicWand,
  ShieldCheck,
  Sparkle,
  Target,
  TrashSimple,
  Warning,
} from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate } from "react-router";

import { ConfirmDelete } from "@/client/components/confirm-delete";
import { ThemeSwitch } from "@/client/components/theme-switch";
import { toast } from "@/client/hooks/use-toast";
import type {
  ApplicationAnswers,
  AtsCategoryKey,
  CareerAnalysis,
  LegitimacyCheck,
  Outreach,
  TailoredResume,
} from "@/client/services/ai/job-match";
import {
  fetchJobAnalysis,
  resumeToPlainText,
  useAnalyzeResume,
  useApplicationAnswers,
  useCoverLetter,
  useDeleteJobAnalysis,
  useJobAnalyses,
  useLegitimacy,
  useOutreach,
  useTailorResumeToJob,
} from "@/client/services/ai/job-match";
import { useCreateApplication } from "@/client/services/applications";
import { useCheckout, useSubscription } from "@/client/services/billing";
import { useResumes } from "@/client/services/resume";

import { NotificationsBell } from "../_components/notifications";

// Job Match is a Pro feature; Autopilot (a higher tier) also includes it.
const PLAN_ORDER: Record<string, number> = { free: 0, pro: 1, autopilot: 2 };

const scoreColor = (score: number) =>
  score >= 75 ? "text-success" : score >= 50 ? "text-warning" : "text-error";

const barColor = (score: number) =>
  score >= 75 ? "bg-success" : score >= 50 ? "bg-warning" : "bg-error";

const List = ({ items, variant }: { items: string[]; variant: "good" | "bad" }) => (
  <ul className="space-y-1.5 text-sm">
    {items.map((item) => (
      <li key={item} className="flex gap-x-2">
        <span className={variant === "good" ? "text-success" : "text-error"}>
          {variant === "good" ? "+" : "−"}
        </span>
        <span className="opacity-90">{item}</span>
      </li>
    ))}
  </ul>
);

export const JobsPage = () => {
  const { i18n } = useLingui();
  const navigate = useNavigate();
  const location = useLocation();
  const { resumes } = useResumes();
  const { analyzeResume, loading } = useAnalyzeResume();
  const { tailorResume, loading: tailoring } = useTailorResumeToJob();
  const { coverLetter, loading: writingLetter } = useCoverLetter();
  const { check: checkLegitimacy, loading: checkingLegit } = useLegitimacy();
  const { generate: generateAnswers, loading: writingAnswers } = useApplicationAnswers();
  const { generate: generateOutreach, loading: writingOutreach } = useOutreach();
  const { create: trackApplication } = useCreateApplication();
  const { analyses } = useJobAnalyses();
  const { deleteAnalysis } = useDeleteJobAnalysis();
  const { subscription, loading: subscriptionLoading } = useSubscription();
  const { checkout, loading: checkingOut } = useCheckout();

  // Job Match is locked when billing is enabled and the plan is below Pro
  // (so Pro AND Autopilot unlock it). While the subscription is still loading
  // we know neither, so we render a placeholder rather than flashing a panel.
  const meetsPro = (PLAN_ORDER[subscription?.plan ?? "free"] ?? 0) >= PLAN_ORDER.pro;
  const locked = !!subscription?.billingEnabled && !meetsPro;

  // The Stripe Checkout return (?checkout=success) is handled globally by
  // BillingReturn in the dashboard layout, which refreshes the whole account.

  const [resumeId, setResumeId] = useState("");
  // Seed from the navigation state on mount so the RichInput (tiptap only reads
  // `content` at creation time) shows a prefilled offer description right away.
  const [jobDescription, setJobDescription] = useState(() => {
    const state = location.state as { jobDescription?: string } | null;
    return state?.jobDescription ?? "";
  });
  const [result, setResult] = useState<CareerAnalysis | null>(null);
  const [tailored, setTailored] = useState<TailoredResume | null>(null);
  const [letter, setLetter] = useState<string | null>(null);
  const [legitimacy, setLegitimacy] = useState<LegitimacyCheck | null>(null);
  const [answers, setAnswers] = useState<ApplicationAnswers | null>(null);
  const [outreach, setOutreach] = useState<Outreach | null>(null);
  const [analysisId, setAnalysisId] = useState<string | null>(null);
  const [jobMeta, setJobMeta] = useState<{
    title: string;
    company: string;
    location?: string | null;
    url?: string | null;
  } | null>(null);
  const [tracked, setTracked] = useState(false);
  const resultRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!resumeId && resumes && resumes.length > 0) setResumeId(resumes[0].id);
  }, [resumes, resumeId]);

  // When a result appears (analysis finished, or a saved one is opened), bring
  // it into view so the user is not left wondering where the output went.
  useEffect(() => {
    if (!result) return;
    const timer = setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => {
      clearTimeout(timer);
    };
  }, [result]);

  // Arriving from an offer (prefill + metadata) or from the tracker (open a
  // saved analysis).
  useEffect(() => {
    const state = location.state as {
      jobDescription?: string;
      jobMeta?: { title: string; company: string; location?: string | null; url?: string | null };
      openAnalysisId?: string;
    } | null;
    if (state?.jobDescription) setJobDescription(state.jobDescription);
    if (state?.jobMeta) setJobMeta(state.jobMeta);
    if (state?.openAnalysisId) void onOpen(state.openAnalysisId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const jobText = jobDescription
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const categoryLabels: Record<AtsCategoryKey, string> = {
    keywords: t`Required keywords`,
    preferred: t`Preferred keywords`,
    contact: t`Contact info`,
    quantification: t`Quantified impact`,
    length: t`Length`,
    verbs: t`Action verbs`,
  };

  const shortlistBadge = (shortlist: "yes" | "maybe" | "no") => {
    if (shortlist === "yes") return { label: t`Shortlisted`, variant: "success" as const };
    if (shortlist === "maybe") return { label: t`On the fence`, variant: "warning" as const };
    return { label: t`Passed`, variant: "error" as const };
  };

  const riskBadge = (risk: "low" | "medium" | "high") => {
    if (risk === "low") return { label: t`Looks legit`, variant: "success" as const };
    if (risk === "medium") return { label: t`Worth checking`, variant: "warning" as const };
    return { label: t`High risk`, variant: "error" as const };
  };

  const onAnalyze = async () => {
    const resume = resumes?.find((item) => item.id === resumeId);
    if (!resume || !jobText) return;

    try {
      setResult(null);
      setTailored(null);
      const analysis = await analyzeResume({
        resume: resumeToPlainText(resume.data),
        jobDescription: jobText,
        locale: i18n.locale,
        resumeId: resume.id,
        resumeTitle: resume.title,
      });
      setLetter(null);
      setLegitimacy(null);
      setAnswers(null);
      setOutreach(null);
      setTracked(false);
      setAnalysisId(analysis.analysisId ?? null);
      setResult(analysis);
      toast({
        variant: "success",
        title: t`Analysis ready`,
        description: t`Here is your expert review below.`,
      });
    } catch (error) {
      toast({
        variant: "error",
        title: t`Oops, the server returned an error.`,
        description: (error as Error).message,
      });
    }
  };

  const onCoverLetter = async () => {
    const resume = resumes?.find((item) => item.id === resumeId);
    if (!resume || !jobText) return;

    try {
      const text = await coverLetter({
        resume: resumeToPlainText(resume.data),
        jobDescription: jobText,
        locale: i18n.locale,
        analysisId: analysisId ?? undefined,
      });
      setLetter(text);
    } catch (error) {
      toast({
        variant: "error",
        title: t`Oops, the server returned an error.`,
        description: (error as Error).message,
      });
    }
  };

  const onLegitimacy = async () => {
    const resume = resumes?.find((item) => item.id === resumeId);
    if (!resume || !jobText) return;
    try {
      const check = await checkLegitimacy({
        resume: resumeToPlainText(resume.data),
        jobDescription: jobText,
        locale: i18n.locale,
        analysisId: analysisId ?? undefined,
      });
      setLegitimacy(check);
    } catch (error) {
      toast({
        variant: "error",
        title: t`Oops, the server returned an error.`,
        description: (error as Error).message,
      });
    }
  };

  const onAnswers = async () => {
    const resume = resumes?.find((item) => item.id === resumeId);
    if (!resume || !jobText) return;
    try {
      const generated = await generateAnswers({
        resume: resumeToPlainText(resume.data),
        jobDescription: jobText,
        locale: i18n.locale,
        analysisId: analysisId ?? undefined,
      });
      setAnswers(generated);
    } catch (error) {
      toast({
        variant: "error",
        title: t`Oops, the server returned an error.`,
        description: (error as Error).message,
      });
    }
  };

  const onOutreach = async () => {
    const resume = resumes?.find((item) => item.id === resumeId);
    if (!resume || !jobText) return;
    try {
      const generated = await generateOutreach({
        resume: resumeToPlainText(resume.data),
        jobDescription: jobText,
        locale: i18n.locale,
        analysisId: analysisId ?? undefined,
      });
      setOutreach(generated);
    } catch (error) {
      toast({
        variant: "error",
        title: t`Oops, the server returned an error.`,
        description: (error as Error).message,
      });
    }
  };

  const onTrack = async () => {
    if (!jobText) return;
    const firstLine = jobText.split(/[\n.]/)[0]?.trim().slice(0, 120) || jobText.slice(0, 120);
    try {
      await trackApplication({
        jobTitle: jobMeta?.title ?? firstLine,
        company: jobMeta?.company || "-",
        location: jobMeta?.location ?? undefined,
        url: jobMeta?.url ?? undefined,
        resumeId: tailored?.resume.id ?? resumeId,
        analysisId: analysisId ?? undefined,
      });
      setTracked(true);
      toast({ variant: "success", title: t`Added to your applications` });
    } catch (error) {
      toast({
        variant: "error",
        title: t`Oops, the server returned an error.`,
        description: (error as Error).message,
      });
    }
  };

  const onOpen = async (id: string) => {
    try {
      const record = await fetchJobAnalysis(id);
      setTailored(null);
      setLetter(record.coverLetter ?? null);
      setLegitimacy(record.legitimacy ?? null);
      setAnswers(record.applicationAnswers ?? null);
      setOutreach(record.outreach ?? null);
      setTracked(false);
      setAnalysisId(record.id);
      setResult(record.result);
    } catch (error) {
      toast({
        variant: "error",
        title: t`Oops, the server returned an error.`,
        description: (error as Error).message,
      });
    }
  };

  const onTailor = async () => {
    if (!resumeId || !jobText) return;

    try {
      const created = await tailorResume({
        resumeId,
        jobDescription: jobText,
        locale: i18n.locale,
      });
      setTailored(created);
      toast({
        variant: "success",
        title: t`Tailored resume created`,
        description: t`You can find it in My Resumes.`,
      });
    } catch (error) {
      toast({
        variant: "error",
        title: t`Oops, the server returned an error.`,
        description: (error as Error).message,
      });
    }
  };

  const hasResumes = !!resumes && resumes.length > 0;
  const hasHistory = !!analyses && analyses.length > 0;
  const showSidebar = hasHistory && !locked && !subscriptionLoading;

  const proFeatures = [
    t`Expert panel: ATS engine, recruiter, resume analyst and interview coach`,
    t`An AI-tailored resume for each job, built from your original`,
    t`Honest attention points before you apply`,
    t`A saved history of all your analyses`,
  ];

  return (
    <>
      <Helmet>
        <title>
          {t`Job Match`} - {t`Izicvs`}
        </title>
      </Helmet>

      <div className={cn("grid gap-6", showSidebar && "lg:grid-cols-[minmax(0,1fr)_20rem]")}>
        <div className="min-w-0 space-y-6">
          {/* Header: title + intro, with the shared notification + theme cluster. */}
          <div className="flex items-start justify-between gap-4">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-1"
            >
              <h1 className="flex items-center gap-x-2 text-3xl font-bold tracking-tight lg:text-4xl">
                <Sparkle weight="fill" className="text-primary" />
                {t`Job Match`}
              </h1>
              <p className="max-w-2xl text-sm opacity-75">
                {t`Get an expert panel to review your resume against a job: a strict ATS engine, a recruiter, a resume analyst and an interview coach.`}
              </p>
            </motion.div>

            <div className="flex shrink-0 items-center gap-x-3">
              <NotificationsBell />
              <ThemeSwitch />
            </div>
          </div>

          {subscriptionLoading && (
            <div className="flex min-h-[calc(100vh-13rem)] items-center justify-center rounded-2xl border bg-secondary-accent/20">
              <CircleNotch size={28} className="animate-spin opacity-40" />
            </div>
          )}

          {!subscriptionLoading && (locked ? (
            <div className="flex min-h-[calc(100vh-13rem)] flex-col items-center justify-center gap-y-5 rounded-2xl border bg-secondary-accent/20 p-8 text-center">
              <div className="mx-auto flex size-14 items-center justify-center rounded-full bg-primary/10">
                <Crown weight="fill" size={28} className="text-primary" />
              </div>
              <div className="space-y-1">
                <h2 className="text-xl font-bold">{t`Job Match is a Pro feature`}</h2>
                <p className="opacity-75">
                  {t`Unlock the full expert panel and AI-tailored resumes.`}
                </p>
              </div>
              <ul className="mx-auto grid max-w-md gap-y-2 text-left text-sm">
                {proFeatures.map((feature) => (
                  <li key={feature} className="flex items-start gap-x-2">
                    <CheckCircle weight="fill" className="mt-0.5 shrink-0 text-success" />
                    <span className="opacity-90">{feature}</span>
                  </li>
                ))}
              </ul>
              <div className="space-y-2">
                <Button size="lg" disabled={checkingOut} onClick={() => checkout("pro")}>
                  {checkingOut ? (
                    <CircleNotch className="mr-2 animate-spin" />
                  ) : (
                    <Crown weight="fill" className="mr-2" />
                  )}
                  {t`Go Pro, €9/month`}
                </Button>
                <p className="text-xs opacity-60">{t`Cancel anytime.`}</p>
              </div>
            </div>
          ) : (hasResumes ? (
            <div className="grid gap-y-4 rounded-2xl border bg-secondary-accent/20 p-5">
              <div className="space-y-1.5">
                <Label>{t`Resume`}</Label>
                <Select value={resumeId} onValueChange={setResumeId}>
                  <SelectTrigger className="rounded-full">
                    <SelectValue placeholder={t`Resume`} />
                  </SelectTrigger>
                  <SelectContent>
                    {resumes.map((resume) => (
                      <SelectItem key={resume.id} value={resume.id}>
                        {resume.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label>{t`Job description`}</Label>
                <RichInput content={jobDescription} onChange={setJobDescription} />
              </div>

              <Button size="lg" disabled={loading || !jobText} onClick={onAnalyze}>
                {loading ? (
                  <CircleNotch className="mr-2 animate-spin" />
                ) : (
                  <Sparkle weight="fill" className="mr-2" />
                )}
                {loading ? t`Consulting the experts...` : t`Run expert analysis`}
              </Button>
            </div>
          ) : (
            <p className="text-sm opacity-75">{t`Create a resume first to use Job Match.`}</p>
          )))}

          {!locked && result && (
            <motion.div
              ref={resultRef}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="scroll-mt-4 space-y-4"
            >
              {/* Verdict hero */}
              <div className="grid gap-4 rounded-xl border p-6 sm:grid-cols-2">
                <div className="flex flex-col items-center justify-center border-b pb-4 sm:border-b-0 sm:border-r sm:pb-0">
                  <span className={cn("text-6xl font-bold", scoreColor(result.ats.score))}>
                    {result.ats.score}%
                  </span>
                  <span className="mt-1 text-xs font-medium uppercase opacity-60">
                    {t`ATS match score`}
                  </span>
                </div>
                <div className="flex flex-col justify-center gap-y-2 sm:pl-2">
                  <Badge
                    variant={shortlistBadge(result.recruiter.shortlist).variant}
                    className="w-fit"
                  >
                    {shortlistBadge(result.recruiter.shortlist).label}
                  </Badge>
                  <p className="text-sm opacity-90">{result.recruiter.verdict}</p>
                </div>
              </div>

              {/* Track this as an application */}
              <Button
                variant={tracked ? "secondary" : "primary"}
                className="w-fit"
                disabled={tracked}
                onClick={onTrack}
              >
                <Kanban weight="fill" className="mr-2" />
                {tracked ? t`Tracked` : t`Track this application`}
              </Button>

              {/* Expert panels */}
              <Accordion type="multiple" defaultValue={["ats", "recruiter"]} className="space-y-3">
                {/* ATS specialist */}
                <AccordionItem value="ats" className="rounded-xl border px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <span className="flex items-center gap-x-3">
                      <Target size={20} className="text-primary" />
                      <span className="font-semibold">{t`ATS Specialist`}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4 pb-4">
                    <div className="grid gap-y-3">
                      {result.ats.categories.map((category) => (
                        <div key={category.key}>
                          <div className="mb-1 flex items-center justify-between text-xs">
                            <span className="font-medium">{categoryLabels[category.key]}</span>
                            <span className="opacity-60">
                              {category.score}% · {category.weight}%
                            </span>
                          </div>
                          <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                            <div
                              className={cn("h-full rounded-full", barColor(category.score))}
                              style={{ width: `${category.score}%` }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>

                    {result.ats.missingKeywords.length > 0 && (
                      <div>
                        <p className="mb-2 text-xs font-medium uppercase opacity-60">
                          {t`Missing keywords`}
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {result.ats.missingKeywords.map((keyword) => (
                            <Badge key={keyword} outline variant="error">
                              {keyword}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>

                {/* Recruiter */}
                <AccordionItem value="recruiter" className="rounded-xl border px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <span className="flex items-center gap-x-3">
                      <Briefcase size={20} className="text-primary" />
                      <span className="font-semibold">{t`Recruiter`}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4 pb-4">
                    {result.recruiter.strengths.length > 0 && (
                      <div>
                        <p className="mb-2 text-xs font-medium uppercase opacity-60">{t`Strengths`}</p>
                        <List items={result.recruiter.strengths} variant="good" />
                      </div>
                    )}
                    {result.recruiter.concerns.length > 0 && (
                      <div>
                        <p className="mb-2 text-xs font-medium uppercase opacity-60">{t`Concerns`}</p>
                        <List items={result.recruiter.concerns} variant="bad" />
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>

                {/* CV analyst */}
                <AccordionItem value="analysis" className="rounded-xl border px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <span className="flex w-full items-center justify-between gap-x-3 pr-2">
                      <span className="flex items-center gap-x-3">
                        <FileMagnifyingGlass size={20} className="text-primary" />
                        <span className="font-semibold">{t`Resume Analyst`}</span>
                      </span>
                      <span
                        className={cn("text-sm font-bold", scoreColor(result.analysis.overall))}
                      >
                        {result.analysis.overall}%
                      </span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4 pb-4">
                    {result.analysis.strengths.length > 0 && (
                      <div>
                        <p className="mb-2 text-xs font-medium uppercase opacity-60">{t`Strengths`}</p>
                        <List items={result.analysis.strengths} variant="good" />
                      </div>
                    )}
                    {result.analysis.weaknesses.length > 0 && (
                      <div>
                        <p className="mb-2 text-xs font-medium uppercase opacity-60">{t`Weaknesses`}</p>
                        <List items={result.analysis.weaknesses} variant="bad" />
                      </div>
                    )}
                    {result.analysis.sectionFeedback.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-medium uppercase opacity-60">{t`Suggested edits`}</p>
                        {result.analysis.sectionFeedback.map((item, index) => (
                          <div key={index} className="rounded-lg border p-3">
                            <p className="text-xs font-semibold uppercase opacity-60">
                              {item.section}
                            </p>
                            <p className="mt-1 text-sm">{item.feedback}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>

                {/* Interview coach */}
                <AccordionItem value="interview" className="rounded-xl border px-4">
                  <AccordionTrigger className="hover:no-underline">
                    <span className="flex items-center gap-x-3">
                      <ChatCircleText size={20} className="text-primary" />
                      <span className="font-semibold">{t`Interview Coach`}</span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4 pb-4">
                    <div className="space-y-2">
                      {result.interview.questions.map((item, index) => (
                        <div key={index} className="rounded-lg border p-3">
                          <p className="text-sm font-medium">{item.question}</p>
                          <p className="mt-1 text-xs italic opacity-60">{item.focus}</p>
                        </div>
                      ))}
                    </div>
                    {result.interview.tips.length > 0 && (
                      <div>
                        <p className="mb-2 text-xs font-medium uppercase opacity-60">
                          {t`Preparation tips`}
                        </p>
                        <ul className="list-disc space-y-1 pl-5 text-sm opacity-90">
                          {result.interview.tips.map((tip) => (
                            <li key={tip}>{tip}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>

              {/* Turn the analysis into an improved resume */}
              <div className="grid gap-y-3 rounded-xl border bg-secondary-accent/20 p-5">
                <div className="space-y-1">
                  <h3 className="flex items-center gap-x-2 font-semibold">
                    <MagicWand weight="fill" className="text-primary" />
                    {t`Generate a tailored resume`}
                  </h3>
                  <p className="text-sm opacity-75">
                    {t`Create a new resume optimized for this job, based on your original. Your original stays untouched, and nothing is invented.`}
                  </p>
                </div>

                {tailored ? (
                  <div className="grid gap-y-3">
                    {tailored.changes.length > 0 && (
                      <div>
                        <p className="mb-1.5 text-xs font-medium uppercase opacity-60">
                          {t`What changed`}
                        </p>
                        <ul className="list-disc space-y-1 pl-5 text-sm opacity-90">
                          {tailored.changes.map((change) => (
                            <li key={change}>{change}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {tailored.attentionPoints.length > 0 && (
                      <div className="rounded-lg border border-warning/40 bg-warning/10 p-3">
                        <p className="mb-1.5 flex items-center gap-x-2 text-xs font-medium uppercase text-warning">
                          <Warning weight="fill" size={14} />
                          {t`Points to review before sending`}
                        </p>
                        <ul className="list-disc space-y-1 pl-5 text-sm opacity-90">
                          {tailored.attentionPoints.map((point) => (
                            <li key={point}>{point}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <Button
                      variant="secondary"
                      className="w-fit"
                      onClick={() => navigate(`/builder/${tailored.resume.id}`)}
                    >
                      {t`Open in the editor`}
                    </Button>
                  </div>
                ) : (
                  <Button className="w-fit" disabled={tailoring} onClick={onTailor}>
                    {tailoring ? (
                      <CircleNotch className="mr-2 animate-spin" />
                    ) : (
                      <MagicWand weight="fill" className="mr-2" />
                    )}
                    {tailoring ? t`Tailoring your resume...` : t`Generate tailored resume`}
                  </Button>
                )}
              </div>

              {/* Cover letter */}
              <div className="grid gap-y-3 rounded-xl border bg-secondary-accent/20 p-5">
                <div className="space-y-1">
                  <h3 className="flex items-center gap-x-2 font-semibold">
                    <Envelope weight="fill" className="text-primary" />
                    {t`Cover letter`}
                  </h3>
                  <p className="text-sm opacity-75">
                    {t`Generate a tailored cover letter for this job, based on your resume.`}
                  </p>
                </div>

                {letter ? (
                  <div className="grid gap-y-3">
                    <div className="whitespace-pre-wrap rounded-lg border bg-background p-4 text-sm leading-relaxed">
                      {letter}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        variant="secondary"
                        className="w-fit"
                        onClick={() => {
                          void navigator.clipboard.writeText(letter);
                          toast({ variant: "success", title: t`Copied to clipboard` });
                        }}
                      >
                        <Copy className="mr-2" />
                        {t`Copy`}
                      </Button>
                      <Button
                        variant="ghost"
                        className="w-fit"
                        disabled={writingLetter}
                        onClick={onCoverLetter}
                      >
                        {writingLetter ? (
                          <CircleNotch className="mr-2 animate-spin" />
                        ) : (
                          <Envelope className="mr-2" />
                        )}
                        {t`Regenerate`}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button className="w-fit" disabled={writingLetter} onClick={onCoverLetter}>
                    {writingLetter ? (
                      <CircleNotch className="mr-2 animate-spin" />
                    ) : (
                      <Envelope weight="fill" className="mr-2" />
                    )}
                    {writingLetter ? t`Writing your letter...` : t`Generate cover letter`}
                  </Button>
                )}
              </div>

              {/* Application answers */}
              <div className="grid gap-y-3 rounded-xl border bg-secondary-accent/20 p-5">
                <div className="space-y-1">
                  <h3 className="flex items-center gap-x-2 font-semibold">
                    <ListChecks weight="fill" className="text-primary" />
                    {t`Application answers`}
                  </h3>
                  <p className="text-sm opacity-75">
                    {t`Draft answers to the common application questions, ready to paste.`}
                  </p>
                </div>

                {answers ? (
                  <div className="grid gap-y-2">
                    {answers.answers.map((qa, index) => (
                      <div key={index} className="rounded-lg border bg-background p-3">
                        <p className="text-sm font-medium">{qa.question}</p>
                        <p className="mt-1 whitespace-pre-wrap text-sm opacity-90">{qa.answer}</p>
                      </div>
                    ))}
                    <Button
                      variant="ghost"
                      className="w-fit"
                      disabled={writingAnswers}
                      onClick={onAnswers}
                    >
                      {writingAnswers ? (
                        <CircleNotch className="mr-2 animate-spin" />
                      ) : (
                        <ListChecks className="mr-2" />
                      )}
                      {t`Regenerate`}
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="secondary"
                    className="w-fit"
                    disabled={writingAnswers}
                    onClick={onAnswers}
                  >
                    {writingAnswers ? (
                      <CircleNotch className="mr-2 animate-spin" />
                    ) : (
                      <ListChecks weight="fill" className="mr-2" />
                    )}
                    {writingAnswers ? t`Drafting...` : t`Draft application answers`}
                  </Button>
                )}
              </div>

              {/* Legitimacy check */}
              <div className="grid gap-y-3 rounded-xl border bg-secondary-accent/20 p-5">
                <div className="space-y-1">
                  <h3 className="flex items-center gap-x-2 font-semibold">
                    <ShieldCheck weight="fill" className="text-primary" />
                    {t`Legitimacy check`}
                  </h3>
                  <p className="text-sm opacity-75">
                    {t`Screen this posting for scam and ghost-job signals.`}
                  </p>
                </div>

                {legitimacy ? (
                  <div className="grid gap-y-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={riskBadge(legitimacy.risk).variant}>
                        {riskBadge(legitimacy.risk).label}
                      </Badge>
                      <p className="text-sm opacity-90">{legitimacy.verdict}</p>
                    </div>
                    {legitimacy.redFlags.length > 0 && (
                      <div>
                        <p className="mb-1.5 text-xs font-medium uppercase opacity-60">
                          {t`Red flags`}
                        </p>
                        <List items={legitimacy.redFlags} variant="bad" />
                      </div>
                    )}
                    {legitimacy.greenFlags.length > 0 && (
                      <div>
                        <p className="mb-1.5 text-xs font-medium uppercase opacity-60">
                          {t`Reassuring signs`}
                        </p>
                        <List items={legitimacy.greenFlags} variant="good" />
                      </div>
                    )}
                  </div>
                ) : (
                  <Button
                    variant="secondary"
                    className="w-fit"
                    disabled={checkingLegit}
                    onClick={onLegitimacy}
                  >
                    {checkingLegit ? (
                      <CircleNotch className="mr-2 animate-spin" />
                    ) : (
                      <ShieldCheck weight="fill" className="mr-2" />
                    )}
                    {checkingLegit ? t`Checking...` : t`Check legitimacy`}
                  </Button>
                )}
              </div>

              {/* Contacts & outreach */}
              <div className="grid gap-y-3 rounded-xl border bg-secondary-accent/20 p-5">
                <div className="space-y-1">
                  <h3 className="flex items-center gap-x-2 font-semibold">
                    <AddressBook weight="fill" className="text-primary" />
                    {t`Contacts & outreach`}
                  </h3>
                  <p className="text-sm opacity-75">
                    {t`Find who to reach at the company and get ready-to-send messages.`}
                  </p>
                </div>

                {outreach ? (
                  <div className="grid gap-y-3">
                    <div className="grid gap-y-1.5">
                      {outreach.contacts.map((contact, index) => (
                        <div key={index} className="rounded-lg border bg-background p-3">
                          <p className="text-sm font-medium">{contact.role}</p>
                          <p className="text-xs opacity-60">{contact.whereToFind}</p>
                          <p className="mt-1 text-xs italic opacity-70">{contact.searchQuery}</p>
                        </div>
                      ))}
                    </div>

                    <div className="rounded-lg border bg-background p-3">
                      <p className="mb-1 flex items-center gap-x-2 text-xs font-medium uppercase opacity-60">
                        <LinkedinLogo weight="fill" size={14} />
                        {t`LinkedIn message`}
                      </p>
                      <p className="whitespace-pre-wrap text-sm opacity-90">
                        {outreach.linkedinMessage}
                      </p>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="mt-1 h-7 px-2 text-xs"
                        onClick={() => {
                          void navigator.clipboard.writeText(outreach.linkedinMessage);
                          toast({ variant: "success", title: t`Copied to clipboard` });
                        }}
                      >
                        <Copy className="mr-1" size={13} />
                        {t`Copy`}
                      </Button>
                    </div>

                    <div className="rounded-lg border bg-background p-3">
                      <p className="mb-1 flex items-center gap-x-2 text-xs font-medium uppercase opacity-60">
                        <Envelope weight="fill" size={14} />
                        {t`Cold email`}
                      </p>
                      <p className="text-sm font-medium">{outreach.email.subject}</p>
                      <p className="mt-1 whitespace-pre-wrap text-sm opacity-90">
                        {outreach.email.body}
                      </p>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="mt-1 h-7 px-2 text-xs"
                        onClick={() => {
                          void navigator.clipboard.writeText(
                            `${outreach.email.subject}\n\n${outreach.email.body}`,
                          );
                          toast({ variant: "success", title: t`Copied to clipboard` });
                        }}
                      >
                        <Copy className="mr-1" size={13} />
                        {t`Copy`}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    variant="secondary"
                    className="w-fit"
                    disabled={writingOutreach}
                    onClick={onOutreach}
                  >
                    {writingOutreach ? (
                      <CircleNotch className="mr-2 animate-spin" />
                    ) : (
                      <AddressBook weight="fill" className="mr-2" />
                    )}
                    {writingOutreach ? t`Finding contacts...` : t`Find contacts`}
                  </Button>
                )}
              </div>
            </motion.div>
          )}
        </div>

        {showSidebar && (
          <aside className="lg:sticky lg:top-4 lg:self-start">
            <div className="grid gap-y-2 rounded-xl border p-4">
              <p className="flex items-center gap-x-2 text-xs font-medium uppercase opacity-60">
                <ClockCounterClockwise size={14} />
                {t`Your past analyses`}
              </p>
              <div className="grid gap-y-1.5">
                {(analyses ?? []).map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-x-3 rounded-lg border p-3 transition-colors hover:bg-secondary-accent/20"
                  >
                    <button
                      type="button"
                      className="min-w-0 flex-1 text-left"
                      onClick={() => onOpen(item.id)}
                    >
                      <p className="line-clamp-1 text-sm font-medium">{item.jobTitle}</p>
                      <p className="text-xs opacity-60">
                        {[
                          item.resumeTitle,
                          new Date(item.createdAt).toLocaleDateString(i18n.locale),
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </p>
                    </button>
                    <span className={cn("text-sm font-bold", scoreColor(item.atsScore))}>
                      {item.atsScore}%
                    </span>
                    <ConfirmDelete
                      title={t`Delete this analysis?`}
                      onConfirm={() => deleteAnalysis(item.id)}
                    >
                      <Button size="icon" variant="ghost" className="shrink-0">
                        <TrashSimple />
                      </Button>
                    </ConfirmDelete>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        )}
      </div>
    </>
  );
};
